import React, { useState, useEffect, useRef } from 'react';
import { useGlobalProperty } from '@/hooks/useGlobalProperty';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { propertyAPI, whatsappAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  MessageSquare,
  Settings,
  Send,
  CheckCircle2,
  AlertCircle,
  Copy,
  ExternalLink,
  Phone,
  Bot,
  User,
  RefreshCw,
  Link2,
  XCircle,
  BarChart3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== OWNER VIEW ====================
function OwnerWhatsAppView() {
  const [properties, setProperties] = useState([]);
  const [statuses, setStatuses] = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await propertyAPI.getAll();
      setProperties(res.data);
      const statusMap = {};
      for (const p of res.data) {
        try {
          const s = await whatsappAPI.getStatus(p.id);
          statusMap[p.id] = s.data;
        } catch { statusMap[p.id] = { active: false, whatsapp_reservations: 0 }; }
      }
      setStatuses(statusMap);
    } catch { toast.error('Error cargando datos'); }
  };

  const hasAnyActive = Object.values(statuses).some(s => s.active);

  return (
    <div className="space-y-6" data-testid="whatsapp-owner-view">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="whatsapp-page-title">
          <MessageSquare className="h-6 w-6 text-green-600" />
          WhatsApp Chatbot
        </h1>
        <p className="text-muted-foreground mt-1">
          Estado del servicio de reservas automáticas por WhatsApp
        </p>
      </div>

      {!hasAnyActive && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">Servicio no activado</p>
                <p className="text-sm text-amber-700 mt-1">
                  El chatbot de WhatsApp debe ser activado por el administrador. Si deseas este servicio, contacta al equipo de soporte para su activación.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {properties.map(p => {
          const status = statuses[p.id] || { active: false, whatsapp_reservations: 0 };
          return (
            <Card key={p.id} data-testid={`whatsapp-status-${p.id}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold">{p.nombre}</h3>
                    <p className="text-sm text-muted-foreground">{p.ciudad}</p>
                  </div>
                  {status.active ? (
                    <Badge className="bg-green-100 text-green-700 border-green-300 flex items-center gap-1" data-testid="whatsapp-active-badge">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Activo
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-gray-500 flex items-center gap-1" data-testid="whatsapp-inactive-badge">
                      <XCircle className="h-3.5 w-3.5" />
                      Inactivo
                    </Badge>
                  )}
                </div>
                {status.active && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-800">
                        <strong>{status.whatsapp_reservations}</strong> reservas por WhatsApp
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ==================== ADMIN VIEW ====================
function AdminWhatsAppView() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [selectedOwner, setSelectedOwner] = useState('');
  const [config, setConfig] = useState({
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_whatsapp_number: '',
    whatsapp_number: '',
    greeting_message: 'Hola, gracias por contactarnos. Te ayudaremos a encontrar el alojamiento ideal.',
    active: true
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('config');

  // Chat simulator
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  useEffect(() => {
    if (selectedOwner) {
      const ownerProps = properties.filter(p => p.user_id === selectedOwner);
      if (ownerProps.length > 0 && !ownerProps.find(p => p.id === selectedProperty)) {
        setSelectedProperty(ownerProps[0].id);
      }
    }
  }, [selectedOwner]);

  useEffect(() => {
    if (selectedProperty) loadConfig(selectedProperty);
  }, [selectedProperty]);

  const loadData = async () => {
    try {
      const [propsRes, usersRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/all-properties`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
        fetch(`${BACKEND_URL}/api/admin/users`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      ]);
      const propsData = await propsRes.json();
      const usersData = await usersRes.json();
      setProperties(Array.isArray(propsData) ? propsData : []);
      setAllUsers(Array.isArray(usersData) ? usersData.filter(u => u.role === 'owner') : []);
    } catch { toast.error('Error cargando datos'); }
  };

  const loadConfig = async (propId) => {
    try {
      const res = await whatsappAPI.getConfig(propId);
      if (res.data && res.data.twilio_account_sid) {
        setConfig(prev => ({ ...prev, ...res.data }));
      } else {
        setConfig({ twilio_account_sid: '', twilio_auth_token: '', twilio_whatsapp_number: '', whatsapp_number: '', greeting_message: 'Hola, gracias por contactarnos. Te ayudaremos a encontrar el alojamiento ideal.', active: true });
      }
    } catch {
      setConfig({ twilio_account_sid: '', twilio_auth_token: '', twilio_whatsapp_number: '', whatsapp_number: '', greeting_message: 'Hola, gracias por contactarnos. Te ayudaremos a encontrar el alojamiento ideal.', active: true });
    }
  };

  const handleSave = async () => {
    if (!selectedProperty || !selectedOwner) {
      toast.error('Selecciona un propietario y una propiedad');
      return;
    }
    if (!config.twilio_account_sid || !config.twilio_auth_token) {
      toast.error('Ingresa las credenciales de Twilio');
      return;
    }
    setSaving(true);
    try {
      await whatsappAPI.saveConfig({ ...config, property_id: selectedProperty, owner_id: selectedOwner });
      toast.success('Configuración guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const ownerProps = selectedOwner ? properties.filter(p => p.user_id === selectedOwner) : [];
  const webhookUrl = `${BACKEND_URL}/api/whatsapp/webhook/${selectedOwner}`;

  const sendTestMessage = async () => {
    if (!chatInput.trim()) return;
    const userMsg = chatInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setChatInput('');
    setChatLoading(true);
    try {
      const res = await whatsappAPI.testChat({ message: userMsg, phone: '+573001234567' });
      setChatMessages(prev => [...prev, { role: 'bot', text: res.data.response, media_urls: res.data.media_urls || [] }]);
    } catch {
      setChatMessages(prev => [...prev, { role: 'bot', text: 'Error al procesar el mensaje.' }]);
    } finally { setChatLoading(false); }
  };

  const tabs = [
    { id: 'config', label: 'Configurar', icon: Settings },
    { id: 'test', label: 'Probar Bot', icon: Bot },
    { id: 'webhook', label: 'Webhook', icon: Link2 },
  ];

  return (
    <div className="space-y-6" data-testid="whatsapp-admin-view">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="whatsapp-page-title">
            <MessageSquare className="h-6 w-6 text-green-600" />
            WhatsApp Chatbot
            <Badge variant="outline" className="text-xs">Admin</Badge>
          </h1>
          <p className="text-muted-foreground mt-1">Configura el chatbot de reservas para cada propietario</p>
        </div>
      </div>

      {/* Owner + Property selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Propietario</Label>
              <select
                value={selectedOwner}
                onChange={(e) => setSelectedOwner(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                data-testid="admin-owner-selector"
              >
                <option value="">-- Seleccionar propietario --</option>
                {allUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Propiedad</Label>
              <select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                disabled={!selectedOwner}
                data-testid="admin-property-selector"
              >
                <option value="">-- Seleccionar propiedad --</option>
                {ownerProps.map(p => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedProperty && (
        <>
          {/* Tabs */}
          <div className="flex border-b gap-0" data-testid="whatsapp-tabs">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`whatsapp-tab-${tab.id}`}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id ? 'border-green-600 text-green-700' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" /> {tab.label}
                </button>
              );
            })}
          </div>

          {activeTab === 'config' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card data-testid="whatsapp-config-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Phone className="h-5 w-5 text-green-600" />
                    Credenciales Twilio
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Account SID</Label>
                    <Input placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxx" value={config.twilio_account_sid}
                      onChange={(e) => setConfig(prev => ({ ...prev, twilio_account_sid: e.target.value }))}
                      data-testid="twilio-sid-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Auth Token</Label>
                    <Input type="password" placeholder="Tu Auth Token" value={config.twilio_auth_token}
                      onChange={(e) => setConfig(prev => ({ ...prev, twilio_auth_token: e.target.value }))}
                      data-testid="twilio-token-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Número WhatsApp Twilio</Label>
                    <Input placeholder="+14155238886" value={config.twilio_whatsapp_number}
                      onChange={(e) => setConfig(prev => ({ ...prev, twilio_whatsapp_number: e.target.value }))}
                      data-testid="twilio-phone-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Número WhatsApp del Propietario (Reservas)</Label>
                    <Input placeholder="+573001234567" value={config.whatsapp_number}
                      onChange={(e) => setConfig(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                      data-testid="owner-phone-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Mensaje de Bienvenida</Label>
                    <Textarea rows={3} value={config.greeting_message}
                      onChange={(e) => setConfig(prev => ({ ...prev, greeting_message: e.target.value }))}
                      data-testid="greeting-message-input" />
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={config.active}
                        onChange={(e) => setConfig(prev => ({ ...prev, active: e.target.checked }))}
                        className="w-4 h-4 rounded" data-testid="chatbot-active-toggle" />
                      <span className="text-sm font-medium">Chatbot Activo</span>
                    </label>
                  </div>
                  <Button onClick={handleSave} disabled={saving} className="w-full bg-slate-800 hover:bg-slate-700" data-testid="save-whatsapp-config-btn">
                    {saving ? 'Guardando...' : 'Guardar Configuración'}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ExternalLink className="h-5 w-5 text-blue-600" />
                    Instrucciones
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {[
                    { n: 1, t: 'Configura las credenciales de Twilio para este propietario' },
                    { n: 2, t: 'Ingresa el número de WhatsApp de reservas del propietario' },
                    { n: 3, t: 'Copia la URL del webhook (pestaña Webhook) y configúrala en Twilio Sandbox' },
                    { n: 4, t: 'Activa el chatbot con el toggle y guarda' },
                    { n: 5, t: 'Prueba el bot en la pestaña "Probar Bot"' },
                  ].map(s => (
                    <div key={s.n} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold">{s.n}</span>
                      <p className="text-muted-foreground">{s.t}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'test' && (
            <Card className="max-w-2xl mx-auto" data-testid="whatsapp-test-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Bot className="h-5 w-5 text-green-600" />
                    Simulador de Chatbot
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setChatMessages([])} data-testid="reset-chat-btn">
                    <RefreshCw className="h-4 w-4 mr-1" /> Reiniciar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-[#e5ddd5] rounded-lg p-4 h-[420px] overflow-y-auto mb-4 space-y-3" data-testid="chat-window">
                  {chatMessages.length === 0 && (
                    <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                      Escribe "hola" para iniciar
                    </div>
                  )}
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                        msg.role === 'user' ? 'bg-[#dcf8c6] text-gray-900' : 'bg-white text-gray-900'
                      }`} data-testid={`chat-msg-${msg.role}-${i}`}>
                        <p className="whitespace-pre-wrap">{msg.text}</p>
                        {msg.media_urls && msg.media_urls.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {msg.media_urls.map((url, j) => (
                              <img key={j} src={`${BACKEND_URL}${url}`} alt="Foto" className="rounded max-w-full max-h-32 object-cover" />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white rounded-lg px-4 py-2 text-sm shadow-sm">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                <div className="flex gap-2">
                  <Input placeholder='Escribe "hola"...' value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendTestMessage()}
                    disabled={chatLoading} data-testid="chat-input" />
                  <Button onClick={sendTestMessage} disabled={chatLoading || !chatInput.trim()}
                    className="bg-slate-800 hover:bg-slate-700" data-testid="chat-send-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'webhook' && (
            <Card data-testid="webhook-info-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Link2 className="h-5 w-5 text-green-600" />
                  URL del Webhook
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Configura esta URL en Twilio Sandbox &gt; "When a message comes in"
                </p>
                <div className="bg-muted/50 border rounded-lg p-3">
                  <Label className="text-xs text-muted-foreground mb-1 block">Webhook URL (POST)</Label>
                  <div className="flex items-center gap-2">
                    <code className="text-sm break-all flex-1 text-green-700" data-testid="webhook-url">{webhookUrl}</code>
                    <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success('Copiado'); }}
                      data-testid="copy-webhook-btn">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ==================== MAIN PAGE (ROLE-BASED) ====================
export default function WhatsAppPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  return (
    <DashboardLayout>
      {isAdmin ? <AdminWhatsAppView /> : <OwnerWhatsAppView />}
    </DashboardLayout>
  );
}
